<script lang="ts">
  // Comparison mode per INTERFACE-SPECIFICATION.md §5.
  //
  // Two products side by side. Surface View shows the marketing-
  // surface aggregation in three rows (Excellent / Good / Fair) with
  // a delta column. Cube View stacks the two cubes plus a marketing-
  // surface bridge row (necessary because AU unsecured and NZ
  // unsecured have different cube shapes).

  import { PRODUCTS, findProduct, productKey } from '../lib/products';
  import type { ProductDescriptor } from '../lib/products';
  import { cachedCatalogue } from '../lib/catalogue-store.svelte';
  import { surfaceState } from '../lib/surface.svelte';
  import { projectMarketingBand, PUBLIC_BANDS } from '../lib/surface-projector';
  import { formatRate, formatAmount, formatRateDelta, formatAmountDelta, deltaCssClass } from '../lib/formatters';
  import CubeTable from './CubeTable.svelte';
  import ProductPicker from './ProductPicker.svelte';

  interface Props {
    leftKey: string;
    rightKey: string;
    onPick: (side: 'left' | 'right', key: string) => void;
  }

  const { leftKey, rightKey, onPick }: Props = $props();

  const left = $derived(productFor(leftKey));
  const right = $derived(productFor(rightKey));

  const leftCatalogue = $derived(left ? cachedCatalogue(left.jurisdiction, left.product) : null);
  const rightCatalogue = $derived(right ? cachedCatalogue(right.jurisdiction, right.product) : null);

  function productFor(key: string): ProductDescriptor | undefined {
    const [j, p] = key.split('/');
    if (!j || !p) return undefined;
    return findProduct(j as ProductDescriptor['jurisdiction'], p as ProductDescriptor['product']);
  }

  const bandLabel: Record<string, string> = { excellent: 'Excellent', good: 'Good', fair: 'Fair' };
</script>

<section class="comparison">
  <header>
    <ProductPicker label="Left" value={leftKey} onchange={(k) => onPick('left', k)} />
    <span class="muted">vs</span>
    <ProductPicker label="Right" value={rightKey} onchange={(k) => onPick('right', k)} />
  </header>

  {#if !leftCatalogue || !rightCatalogue}
    <p class="muted">Loading…</p>
  {:else if surfaceState.viewDepth === 'surface'}
    <h3 class="section-heading">Public-band aggregation</h3>
    <table class="cube-table">
      <thead>
        <tr>
          <th>Public band</th>
          <th class="num">% — {left?.label} (min)</th>
          <th class="num">$ — {left?.label} (max)</th>
          <th class="num">% — {right?.label} (min)</th>
          <th class="num">$ — {right?.label} (max)</th>
          <th class="num">Δ %</th>
          <th class="num">Δ $</th>
        </tr>
      </thead>
      <tbody>
        {#each PUBLIC_BANDS as band (band)}
          {@const lp = projectMarketingBand(leftCatalogue, band)}
          {@const rp = projectMarketingBand(rightCatalogue, band)}
          {@const rateDelta = lp.min_rate !== null && rp.min_rate !== null ? rp.min_rate - lp.min_rate : null}
          {@const amountDelta = lp.max_amount !== null && rp.max_amount !== null ? rp.max_amount - lp.max_amount : null}
          <tr>
            <td>{bandLabel[band]}</td>
            <td class="num">{formatRate(lp.min_rate)}</td>
            <td class="num">{formatAmount(lp.max_amount)}</td>
            <td class="num">{formatRate(rp.min_rate)}</td>
            <td class="num">{formatAmount(rp.max_amount)}</td>
            <td class="num {deltaCssClass(rateDelta)}">{formatRateDelta(rateDelta)}</td>
            <td class="num {deltaCssClass(amountDelta)}">{formatAmountDelta(amountDelta)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <div class="comparison__cubes">
      <div>
        <h3 class="section-heading">{left?.label} — {left?.cubeShapeLabel}</h3>
        <CubeTable catalogue={leftCatalogue} channel="hLoan" />
      </div>
      <div>
        <h3 class="section-heading">{right?.label} — {right?.cubeShapeLabel}</h3>
        <CubeTable catalogue={rightCatalogue} channel="hLoan" />
      </div>
    </div>
    <div class="comparison__bridge">
      <h3 class="section-heading">Bridge — Marketing-surface aggregation</h3>
      <table class="cube-table">
        <thead>
          <tr>
            <th>Public band</th>
            <th class="num">% — {left?.label} min</th>
            <th class="num">% — {right?.label} min</th>
            <th class="num">Δ %</th>
          </tr>
        </thead>
        <tbody>
          {#each PUBLIC_BANDS as band (band)}
            {@const lp = projectMarketingBand(leftCatalogue, band)}
            {@const rp = projectMarketingBand(rightCatalogue, band)}
            {@const d = lp.min_rate !== null && rp.min_rate !== null ? rp.min_rate - lp.min_rate : null}
            <tr>
              <td>{bandLabel[band]}</td>
              <td class="num">{formatRate(lp.min_rate)}</td>
              <td class="num">{formatRate(rp.min_rate)}</td>
              <td class="num {deltaCssClass(d)}">{formatRateDelta(d)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>

<style>
  .comparison header {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
  }
  .comparison__cubes {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-5);
  }
  @media (min-width: 1280px) {
    .comparison__cubes {
      grid-template-columns: 1fr 1fr;
    }
  }
  .comparison__bridge { margin-top: var(--space-5); }
</style>
